import React from 'react';
import {Link} from 'react-router-dom';
import { Box, Icons, Typography } from '@curves/ui-base';

import {ProductCard} from '@Local/Components/InfoCard';
import {parseDate} from '@Util/moment.util';

import {formatCurrencyFn, isEmpty, isFunction} from '@Util/utils';
import {ORDERS_VIEW_ROUTER_URL} from '@Router/routerPath';
import {DATE_TIME_STANDARD} from '@Constants/appConfig';
/**
 * 取得訂單操作選單對應表
 *
 * 功能：
 * - 根據訂單權限和狀態產生操作選單
 * - 包含查看、折讓、作廢、重新開立發票等操作
 * - 支援條件式顯示和點擊事件
 *
 * @param {Object} params - 訂單參數
 * @param {string|number} params.orderID - 訂單 ID
 * @param {boolean} [params.canReview=true] - 是否可查看
 * @param {boolean} params.canAllowance - 是否可折讓
 * @param {boolean} params.canInvalid - 是否可作廢
 * @param {boolean} params.canReissuing - 是否可重新開立發票
 * @param {Object} extend - 擴展參數
 * @param {Object} extend.dialogRef - 彈窗引用
 * @param {Function} extend.refresh - 刷新回調
 * @returns {Array<Object>} 操作選單陣列
 */
export const getOrderMenuMap = ({orderID, canReview = true, canAllowance, canInvalid, canReissuing} = {}, extend) => {
    const {dialogRef, refresh} = extend || {};
    const {openOrderAllowanceDialog, openOrderAllowanceVoidDialog, openOrderInvoiceReissuingDialog} = dialogRef.current || {};
    return [
        {
            text: '查看',
            condition: canReview,
            component: Link,
            to: ORDERS_VIEW_ROUTER_URL(orderID),
        },
        {
            text: '訂單折讓',
            condition: canAllowance,
            onClick: () => openOrderAllowanceDialog({orderID}, refresh),
        },
        {
            text: '作廢發票',
            condition: canInvalid,
            onClick: () => openOrderAllowanceVoidDialog({orderID}, refresh),
        },
        {
            text: '重開發票',
            condition: canReissuing,
            onClick: () => openOrderInvoiceReissuingDialog({orderID}, refresh),
        },
    ]
};
/**
 * 過濾選單項目
 *
 * 功能：
 * - 根據條件過濾選單項目
 * - 支援函數和布林值條件
 * - 移除條件屬性後返回
 *
 * @param {Array<Object>} menuList - 選單項目陣列
 * @returns {Array<Object>} 過濾後的選單項目陣列
 */
export const filterMenu = (menuList) => {
    return menuList.filter(({ condition }) => {
        if (isFunction(condition)) return condition() || false;
        return condition;
    }).map((item) => {
            delete item.condition;
            return item;
        });
}
/**
 * 渲染退貨項目
 *
 * @param {Array} returnItems - 退貨項目陣列
 * @param {Function} renderFn - 渲染函數
 * @param {boolean} [showIcon=false] - 是否顯示圖示
 * @returns {JSX.Element|null} 渲染結果
 */
const renderReturnItems = (returnItems, renderFn, showIcon = false) => {
    if (isEmpty(returnItems)) return null;
    return returnItems.map((item) => (
        <Box className="return-qty" key={item.orderReturnItemID}>
            {showIcon && <Icons.Reply/>}
            <span>
                {renderFn(item)}
            </span>
        </Box>
    ));
};
/**
 * 取得訂單商品項目表格欄位配置
 *
 * 功能：
 * - 定義訂單商品項目的表格顯示格式
 * - 包含商品資訊、價格、數量等欄位
 * - 支援退貨項目顯示
 *
 * @returns {Array<Object>} 表格欄位配置陣列
 */
export const getOrderProductItemRowLabel = () => {
    return [
        {
            cellKey: 'orderItemID',
            headerLabel: '品項',
            className: 'vertical-align-middle',
            minWidth: 160,
            formatCell: (cellValue, {productName, variantName, sku}) => {
                const displayName = variantName ? `${productName} ${variantName}` : productName;
                return <ProductCard productName={displayName} variantName={variantName} sku={sku}/>;
            },
        },
        {
            cellKey: 'productTypeText',
            headerLabel: '類型',
            align: 'center',
            className: 'vertical-align-middle',
            formatCell: (cellValue, row) => (
                <>
                    <Typography className="mb-0">{cellValue}</Typography>
                    {renderReturnItems(row.returnItems, (item) => parseDate(item.orderReturnDate, DATE_TIME_STANDARD.DISPLAYDATEBYSLASH), true)}
                </>
            ),
        },
        {
            cellKey: 'unitPrice',
            headerLabel: '價格',
            align: 'right',
            minWidth: 100,
            className: 'vertical-align-middle',
            formatCell: (cellValue, row) => (
                <>
                    <Typography className="mb-0">
                        {formatCurrencyFn(cellValue)}
                    </Typography>
                    {renderReturnItems(row.returnItems, (item) => formatCurrencyFn(item.returnUnitPrice))}
                </>
            ),
        },
        {
            cellKey: 'quantity',
            headerLabel: '數量',
            align: 'right',
            minWidth: 80,
            className: 'vertical-align-middle',
            formatCell: (cellValue, row) => (
                <>
                    <Typography className="mb-0">{cellValue}</Typography>
                    {renderReturnItems(row.returnItems, (item) => formatCurrencyFn(item.returnQuantity))}
                </>
            ),
        },
        {
            cellKey: 'totalPrice',
            headerLabel: '總計',
            align: 'right',
            minWidth: 100,
            className: 'vertical-align-middle',
            formatCell: (cellValue, row) => (
                <>
                    <Typography className="mb-0">
                        {formatCurrencyFn(cellValue)}
                    </Typography>
                    {renderReturnItems(row.returnItems, (item) => formatCurrencyFn(item.returnTotalPrice))}
                </>
            ),
        },
        {
            cellKey: 'notes',
            headerLabel: '備註',
            className: 'vertical-align-middle',
            formatCell: (cellValue) => {
                return cellValue;
            },
        },
    ];
};
/**
 * 取得訂單費用項目表格欄位配置
 *
 * 功能：
 * - 定義訂單費用項目的表格顯示格式
 * - 包含費用名稱、金額等欄位
 * - 支援格式化顯示
 *
 * @returns {Array<Object>} 表格欄位配置陣列
 */
export const getOrderFeeItemRowLabel = () => {
    return [
        {
            cellKey: 'productID',
            headerLabel: '品項',
            className: 'vertical-align-middle',
            formatCell: (cellValue, {pictureSrc, productName}) => {
                return <ProductCard productName={productName}/>;
            },
        },
        {
            cellKey: 'productTypeText',
            headerLabel: '類型',
            align: 'center',
            className: 'vertical-align-middle',
            formatCell: (cellValue, row) => (
                <>
                    <Typography className="mb-0">{cellValue}</Typography>
                    {renderReturnItems(row.returnItems, (item) => parseDate(item.orderReturnDate, DATE_TIME_STANDARD.DISPLAYDATEBYSLASH), true)}
                </>
            ),
        },
        {
            cellKey: 'unitPrice',
            headerLabel: '價格',
            align: 'right',
            minWidth: 100,
            className: 'vertical-align-middle',
            formatCell: (cellValue, row) => (
                <>
                    <Typography className="mb-0">{formatCurrencyFn(cellValue)}</Typography>
                    {renderReturnItems(row.returnItems, (item) => formatCurrencyFn(item.returnUnitPrice))}
                </>
            ),
        },
        {
            cellKey: 'notes',
            headerLabel: '備註',
            className: 'vertical-align-middle',
            formatCell: (cellValue) => {
                return cellValue;
            },
        },
    ]
}
